import React, { useState } from 'react';
import { Button } from '@mui/material';
import { AddShoppingCart, Check } from '@mui/icons-material';
import { Product } from '../../src/types';
import { useCart } from '../../src/context/CartContext';
import { getDefaultCaseQuantity, getCaseQuantityDisplayText } from '../../src/utils/caseQuantity';
import { colors } from '../../src/utils/constants';
import { useTheme } from '@mui/material/styles';

interface AddToCartButtonProps {
  product: Product;
  size?: 'small' | 'medium' | 'large';
}

const AddToCartButton: React.FC<AddToCartButtonProps> = ({ product, size = 'medium' }) => {
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isItemAdded, setIsItemAdded] = useState(false);
  const { addToCart } = useCart();
  const theme = useTheme();

  const handleAddToCart = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsAddingToCart(true);
    const defaultCaseQuantity = getDefaultCaseQuantity(product);
    await addToCart(product.id, defaultCaseQuantity, 1);

    // Start checkmark animation
    setIsItemAdded(true);
    setTimeout(() => setIsAddingToCart(false), 300); // Scale animation duration
    setTimeout(() => setIsItemAdded(false), 800); // Checkmark display duration
  };

  const buttonText = size === 'small' 
    ? (isItemAdded ? 'Added!' : 'Add to Cart')
    : (isItemAdded ? 'Added!' : `Add ${getCaseQuantityDisplayText(getDefaultCaseQuantity(product))} to Cart`);

  return (
    <Button
      variant="contained"
      color="primary"
      size={size}
      startIcon={isItemAdded ? <Check /> : <AddShoppingCart />}
      onClick={handleAddToCart}
      fullWidth
      sx={{
        fontWeight: 600,
        color: '#fff',
        bgcolor: isItemAdded ? theme.palette.success.main : theme.palette.primary.main,
        borderRadius: '0 0 12px 12px',
        py: size === 'small' ? 1 : 1.5,
        transition: 'all 0.8s ease-in-out',
        transform: isAddingToCart ? 'scale(1.05)' : 'scale(1)',
        ...(!isItemAdded && {
          '&:hover': {
            bgcolor: theme.palette.primary.dark,
            transform: 'scale(1.02)',
          },
        }),
      }}
    >
      {buttonText}
    </Button>
  );
};

export default AddToCartButton;
